import {inject, Injectable} from '@angular/core';
import {
  NbAuthResult,
  NbAuthSimpleToken,
  NbAuthStrategy,
  NbAuthStrategyClass,
  NbAuthStrategyOptions,
} from '@nebular/auth';
import {Observable, of} from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';

import {MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService} from '@azure/msal-angular';
import {
  AuthenticationResult,
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PopupRequest,
  PublicClientApplication
} from '@azure/msal-browser';
import {APIService} from '../api';

export interface MicrosoftSigninStrategyOptions extends NbAuthStrategyOptions {
  // clientId: string;
  redirect?: {
    success?: string,
  };
}

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(logLevel, message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: '00356578-da95-4641-a02d-e932c1cbaf88',
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      }
    }
  });
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read']
    },
    loginFailedRoute: '/login-failed'
  };
}

/**
 * Strategy that spawns a Microsoft OAuth2 process and then call our backend API
 * to retrieve a user token.
 */
@Injectable()
export class MicrosoftSigninStrategy extends NbAuthStrategy {

  private msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG)

  private authService = inject(MsalService)

  private api = inject(APIService)
  // private ngZone = inject(NgZone)

  protected defaultOptions: MicrosoftSigninStrategyOptions = {
    name: 'microsoft-signin',
    // clientId: null,
    token: {
      name: 'token',
      class: NbAuthSimpleToken,
    },
    redirect: {
      success: '/',
    },
  };

  static setup(options: MicrosoftSigninStrategyOptions): [NbAuthStrategyClass, MicrosoftSigninStrategyOptions] {
    return [MicrosoftSigninStrategy, options];
  }

  private loginPopup(): Observable<AuthenticationResult> {
    if (this.msalGuardConfig.authRequest) {
      return this.authService.loginPopup({...this.msalGuardConfig.authRequest} as PopupRequest)
    }
    return this.authService.loginPopup()
  }

  authenticate(): Observable<NbAuthResult> {
    return this.authService.initialize().pipe(
      switchMap(() => this.loginPopup()),
      switchMap((response) => {
        return this.api.users_microsoft_login({
          accessToken: response.accessToken,
          email: response.account.username,
        });
      }),
      switchMap((response) => {
        if (response.token) {
          return of(new NbAuthResult(
          true,
          {},
          this.getOption('redirect.success'),
          null,
          null,
          this.createToken<NbAuthSimpleToken>(response.token),
        ));
        } else {
          const errorMessage = response.error;
          console.warn("errorMessage", errorMessage)
          return of(new NbAuthResult(
            false,
            {},
            null,
            // Ignore popup closed error
            errorMessage !== 'popup_closed_by_user' ? errorMessage : null,
            null,
            null,
          ))
        }
      }),
      catchError((err) => {
        console.warn("error:", err)
        const errorMessage = err.error.detail;
        return of(new NbAuthResult(
          false,
          {},
          null,
          // Ignore popup closed error
          errorMessage !== 'popup_closed_by_user' ? errorMessage : null,
          null,
          null,
        ))
      }),
    );
  }

  register(_data?: any): Observable<NbAuthResult> {
    throw new Error('`register` is not supported by `TokenStrategy`.');
  }

  requestPassword(_data?: any): Observable<NbAuthResult> {
    throw new Error('`requestPassword` is not supported by `TokenStrategy`.');
  }

  resetPassword(_data?: any): Observable<NbAuthResult> {
    throw new Error('`resetPassword` is not supported by `TokenStrategy`.');
  }

  logout(): Observable<NbAuthResult> {
    return of(new NbAuthResult(true));
  }

  refreshToken(_data?: any): Observable<NbAuthResult> {
    throw new Error('`refreshToken` is not supported by `TokenStrategy`.');
  }

}
